import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { questionsService } from '@/services/questions.service';
import { coursesService } from '@/services/courses.service';
import Swal from 'sweetalert2';
import { confirmDelete, showDeleteSuccess, showDeleteError } from '@/utils/deleteConfirmation';
import {
  Database,
  Search,
  Filter,
  Download,
  Eye,
  Edit3,
  Trash2,
  Calendar,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  BookOpen,
  Target,
  Lightbulb,
  RefreshCw,
  Code2,
} from 'lucide-react';
import { ViewToggle } from '@/components/ViewToggle';
import { PaginationControls } from '@/components/PaginationControls';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { Tooltip } from '@/components/Tooltip';
import { StatusBadge } from '@/components/Badge';
import { QuestionSourceInfo, hasQuestionSourceInfo } from '@/components/QuestionSourceInfo';
import { SortableTable, Column } from '@/components/SortableTable';
import { InteractiveQuestionPlayer } from '@/components/InteractiveQuestionPlayer';
// import { exportToCSV } from '@/utils/exportCSV'; // Commented out - CSV export currently hidden
import { storage } from '@/utils/localStorage';
import type { Question } from '@/types';
import { formatAlternativeText } from '@/utils/textFormatting';

const EXAMS = [
  // ANBIMA Certifications
  'AI', 'CPA', 'CPro-R', 'CPro-I', 'CGA', 'CGE', 'CFG',
  // APIMEC RPPS
  'RPPS-BASICO', 'RPPS-INTERMEDIARIO', 'RPPS-AVANCADO',
  // CNPI
  'CNPI-CB', 'CNPI-CG', 'CNPI-CT', 'CNPI-PLENO', 'CNPI-FUNDAMENTALISTA',
  // CFP
  'CFP-MOD1', 'CFP-MOD2', 'CFP-MOD3',
  // ICSS
  'ICSS-ADM', 'ICSS-INVEST',
  // CA - Real Estate
  'CA-600', 'CA-600-HIS', 'CA-300-400', 'CA-300-400-HIS',
  // PQO - B3
  'PQO-OPERACOES', 'PQO-COMERCIAL', 'PQO-COMPLIANCE', 'PQO-RISCO',
  'PQO-BACKOFFICE', 'PQO-LIQUIDACAO', 'PQO-CUSTODIA',
  // Other
  'FBB-OUVIDORIA'
];

export function QuestionBank() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Load preferences from localStorage
  const prefs = storage.getPreferences();

  const [searchTerm, setSearchTerm] = useState('');
  const [examFilter, setExamFilter] = useState(storage.getFormValue('questionBank', 'examFilter') || 'all');
  const [courseFilter, setCourseFilter] = useState(storage.getFormValue('questionBank', 'courseFilter') || 'all');
  const [oeCodeFilter, setOeCodeFilter] = useState(storage.getFormValue('questionBank', 'oeCodeFilter') || '');
  const [typeFilter, setTypeFilter] = useState(storage.getFormValue('questionBank', 'typeFilter') || 'all');
  const [difficultyFilter, setDifficultyFilter] = useState(storage.getFormValue('questionBank', 'difficultyFilter') || 'all');
  const [statusFilter, setStatusFilter] = useState<string>(() => {
    const saved = storage.getFormValue('questionBank', 'statusFilter');
    console.log('[QuestionBank] Initial statusFilter from localStorage:', saved);
    return (typeof saved === 'string' ? saved : null) || 'all';
  });
  const [generatedByFilter, setGeneratedByFilter] = useState('all');
  // NQG-RQ-26 v2: single main bank with flagging — filter by question source
  const [sourceFilter, setSourceFilter] = useState(storage.getFormValue('questionBank', 'sourceFilter') || 'all');
  const [view, setView] = useState<'grid' | 'list'>(prefs.defaultView || 'list');
  const [paginationMode, setPaginationMode] = useState<'pagination' | 'infinite'>(
    prefs.defaultPaginationMode || 'pagination'
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [displayCount, setDisplayCount] = useState(20);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [caseStudySiblings, setCaseStudySiblings] = useState<Question[]>([]);
  // Removed expandedOption state - alternatives are no longer clickable
 const [expandedOECodes, setExpandedOECodes] = useState<Set<string>>(new Set());
  const itemsPerPage = prefs.itemsPerPage || 20;

  // Excel Export Range
  const [exportFromQuestion, setExportFromQuestion] = useState<number>(1);
  const [exportToQuestion, setExportToQuestion] = useState<number>(0); // 0 means export all

  // Excel Export Date Range
  const [exportFromDate, setExportFromDate] = useState<string>('');
  const [exportToDate, setExportToDate] = useState<string>('');

  // Save filter preferences when they change
  useEffect(() => {
    storage.rememberFormValue('questionBank', 'examFilter', examFilter);
    storage.rememberFormValue('questionBank', 'courseFilter', courseFilter);
    storage.rememberFormValue('questionBank', 'oeCodeFilter', oeCodeFilter);
    storage.rememberFormValue('questionBank', 'typeFilter', typeFilter);
    storage.rememberFormValue('questionBank', 'difficultyFilter', difficultyFilter);
    storage.rememberFormValue('questionBank', 'statusFilter', statusFilter);
    storage.rememberFormValue('questionBank', 'sourceFilter', sourceFilter);
  }, [examFilter, courseFilter, oeCodeFilter, typeFilter, difficultyFilter, statusFilter, sourceFilter]);

  // Save view and pagination preferences
  useEffect(() => {
    storage.setPreferences({ defaultView: view, defaultPaginationMode: paginationMode });
  }, [view, paginationMode]);

  const { data: questionsData, isLoading } = useQuery({
    queryKey: ['questions', { status: statusFilter, sourceType: sourceFilter }],
    queryFn: async () => {
      try {
        // Don't send status filter if it's "all" - let backend return everything
        const filters: Record<string, any> = statusFilter === 'all' ? { limit: 10000 } : { status: statusFilter, limit: 10000 };
        // NQG-RQ-26 v2: filter by question source (standard vs free PDF)
        if (sourceFilter !== 'all') filters.sourceType = sourceFilter;
        console.log('[QuestionBank] Fetching with filters:', filters);
        console.log('[QuestionBank] Status filter value:', statusFilter);
        const response = await questionsService.getQuestions(filters);
        console.log('[QuestionBank] API response:', response);
        console.log('[QuestionBank] Response data structure:', {
          hasData: !!response?.data,
          hasQuestions: !!response?.data?.questions,
          questionsType: Array.isArray(response?.data?.questions) ? 'array' : typeof response?.data?.questions,
          questionsLength: response?.data?.questions?.length || 0
        });
        // Backend returns: { data: { questions: [...], total, page, limit, pages } }
        // Same structure as old frontend
        const questions = response?.data?.questions || [];
        console.log('[QuestionBank] Extracted questions:', questions.length, 'items');
        if (questions.length > 0) {
          console.log('[QuestionBank] Sample question:', questions[0]);
        }
        return Array.isArray(questions) ? questions : [];
      } catch (error) {
        console.error('[QuestionBank] Failed to fetch questions:', error);
        return [];
      }
    },
  });

  // Fetch courses for the course filter
  const { data: coursesData } = useQuery({
    queryKey: ['courses'],
    queryFn: async () => {
      try {
        const courses = await coursesService.getCourses();
        return Array.isArray(courses) ? courses : [];
      } catch (error) {
        console.error('[QuestionBank] Failed to fetch courses:', error);
        return [];
      }
    },
  });

  // Delete question mutation
  const deleteMutation = useMutation({
    mutationFn: (questionId: string) => questionsService.deleteQuestion(questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      showDeleteSuccess('The question has been successfully deleted.');
    },
    onError: (error: any) => {
      showDeleteError(error.response?.data?.error || 'Failed to delete question');
    },
  });

  // Delete all questions mutation
  const deleteAllMutation = useMutation({
    mutationFn: () => questionsService.deleteAllQuestions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      showDeleteSuccess('All questions have been successfully deleted from the database.');
    },
    onError: (error: any) => {
      showDeleteError(error.response?.data?.error || 'Failed to delete all questions');
    },
  });

  const handleDelete = async (questionId: string) => {
    const confirmed = await confirmDelete({
      title: 'Delete Question?',
      text: 'This action cannot be undone. The question will be permanently deleted.',
    });

    if (confirmed) {
      deleteMutation.mutate(questionId);
    }
  };

  const handleDeleteAll = async () => {
    const confirmed = await confirmDelete({
      title: 'Delete ALL Questions?',
      text: `This action cannot be undone! All ${allQuestions.length} questions will be permanently deleted from the database.`,
      confirmButtonText: 'Yes, Delete All',
      cancelButtonText: 'No, Cancel',
    });

    if (confirmed) {
      deleteAllMutation.mutate();
    }
  };

  // Apply all filters - ensure allQuestions is always an array
  const allQuestions = Array.isArray(questionsData) ? questionsData : [];
  const filteredQuestions = allQuestions.filter((q: any) => {
    const matchesSearch =
      searchTerm === '' ||
      q.question_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.question_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.explanation?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesExam = examFilter === 'all' || q.exam_id === examFilter;
    const matchesCourse = courseFilter === 'all' || q.course === courseFilter;
    const matchesOeCode =
      oeCodeFilter === '' ||
      (q.oeCode && q.oeCode.toLowerCase().includes(oeCodeFilter.toLowerCase())) ||
      (q.oeCodes && q.oeCodes.some((code: string) => code.toLowerCase().includes(oeCodeFilter.toLowerCase())));
    const matchesType = typeFilter === 'all' || q.question_type === typeFilter;
    const matchesDifficulty = difficultyFilter === 'all' || q.difficulty === difficultyFilter;
    const matchesStatus = statusFilter === 'all' || q.status === statusFilter;
    const matchesGeneratedBy = generatedByFilter === 'all' || q.generated_by === generatedByFilter;
    // NQG-RQ-26 v2: questions without a sourceType are standard-bank questions
    const matchesSource = sourceFilter === 'all' || (q.sourceType || 'standard') === sourceFilter;

    return (
      matchesSearch &&
      matchesExam &&
      matchesCourse &&
      matchesOeCode &&
      matchesType &&
      matchesDifficulty &&
      matchesStatus &&
      matchesGeneratedBy &&
      matchesSource
    );
  });

  const totalItems = filteredQuestions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Display questions based on mode
  const paginatedQuestions = useMemo(() => {
    if (paginationMode === 'infinite') {
      return filteredQuestions.slice(0, displayCount);
    } else {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return filteredQuestions.slice(startIndex, endIndex);
    }
  }, [filteredQuestions, paginationMode, displayCount, currentPage, itemsPerPage]);

  // Stats
  const stats = {
    total: filteredQuestions.length,
    approved: filteredQuestions.filter((q) => q.status === 'approved').length,
    underReview: filteredQuestions.filter((q) => q.status === 'under_review').length,
    draft: filteredQuestions.filter((q) => q.status === 'draft').length,
    avgQuality:
      filteredQuestions.reduce((sum, q) => sum + ((q.validationScore || 0) * 10), 0) /
        filteredQuestions.length || 0,
  };

  const handleLoadMore = () => {
    setDisplayCount((prev) => prev + itemsPerPage);
  };

  const handlePaginationModeChange = (mode: 'pagination' | 'infinite') => {
    setPaginationMode(mode);
    if (mode === 'pagination') {
      setCurrentPage(1);
    } else {
      setDisplayCount(itemsPerPage);
    }
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setExamFilter('all');
    setCourseFilter('all');
    setOeCodeFilter('');
    setTypeFilter('all');
    setDifficultyFilter('all');
    setStatusFilter('all');
    setGeneratedByFilter('all');
    setSourceFilter('all');
    setCurrentPage(1);
    setDisplayCount(itemsPerPage);
  };

  const handleViewDetails = (question: Question) => {
    setSelectedQuestion(question);
    setShowDetailsModal(true);

    // For case study questions, find all sibling sub-questions
    if (question.question_type === 'case_study' || question.question_type === 'case_based') {
      const siblings = allQuestions.filter((q: Question) => {
        if (q.question_id === question.question_id) return false;
        if ((q.question_type !== 'case_study' && q.question_type !== 'case_based')) return false;
        // Match by parentCaseId
        if (question.parentCaseId && q.parentCaseId === question.parentCaseId) return true;
        // Match by linkedQuestionIds (if this is a parent)
        if (question.linkedQuestionIds?.includes(q.question_id)) return true;
        // Match by same caseTitle + exam (fallback for legacy data)
        if (question.caseTitle && q.caseTitle === question.caseTitle && q.exam_id === question.exam_id) return true;
        return false;
      });

      // Combine current question with siblings and sort by subQuestionIndex or caseQuestionNumber
      const allSubQuestions = [question, ...siblings].sort((a, b) => {
        const idxA = a.subQuestionIndex || a.caseQuestionNumber || 0;
        const idxB = b.subQuestionIndex || b.caseQuestionNumber || 0;
        return idxA - idxB;
      });
      setCaseStudySiblings(allSubQuestions);
    } else {
      setCaseStudySiblings([]);
    }

    // No longer need to reset expandedOption - alternatives are not clickable
    storage.addRecentlyViewed('question', question.question_id, question.question_text);
  };

  // Removed handleOptionClick - alternatives are no longer clickable
  // All explanations are now shown in unified format below alternatives

  /* CSV export function - currently hidden but kept for future use
  const handleExportCSV = () => {
    if (filteredQuestions.length === 0) {
      Swal.fire({
        title: 'No Questions',
        text: 'There are no questions to export.',
        icon: 'warning',
        confirmButtonColor: '#1e73be',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
          confirmButton: 'rounded-lg px-6 py-3 font-semibold',
        },
      });
      return;
    }

    // Generate timestamp for filename (format: 2024-01-15_14-30-25 - date with time)
    const now = new Date();
    const date = now.toISOString().split('T')[0]; // 2024-01-15
    const time = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // 14-30-25
    const timestamp = `${date}_${time}`;

    // Prepare data with all question details
    const exportData = filteredQuestions.map(q => ({
      question_id: q.question_id,
      exam_id: q.exam_id,
      topic_id: q.topic_id,
      question_type: q.question_type,
      difficulty: q.difficulty,
      status: q.status,
      question_text: q.question_text || (q as any).stem || '',
      option_a: q.alternatives?.find((a: any) => a.letter === 'A')?.text || (q as any).options?.A || '',
      option_b: q.alternatives?.find((a: any) => a.letter === 'B')?.text || (q as any).options?.B || '',
      option_c: q.alternatives?.find((a: any) => a.letter === 'C')?.text || (q as any).options?.C || '',
      option_d: q.alternatives?.find((a: any) => a.letter === 'D')?.text || (q as any).options?.D || '',
      option_e: q.alternatives?.find((a: any) => a.letter === 'E')?.text || '',
      correct_answer: q.alternatives?.find((a: any) => a.isCorrect)?.letter || (q as any).correct_answer || q.correct_option || '',
      explanation: q.explanation || '',
      quality_score: q.quality_score || 0,
      generated_by: q.generated_by || 'system',
      created_at: q.created_at ? new Date(q.created_at).toLocaleString('pt-BR') : '',
      updated_at: q.updated_at ? new Date(q.updated_at).toLocaleString('pt-BR') : '',
    }));

    exportToCSV(
      exportData,
      `question-bank-${timestamp}`,
      [
        { key: 'question_id', label: 'Question ID' },
        { key: 'exam_id', label: 'Exam' },
        { key: 'topic_id', label: 'Topic' },
        { key: 'question_type', label: 'Type' },
        { key: 'difficulty', label: 'Difficulty' },
        { key: 'status', label: 'Status' },
        { key: 'question_text', label: 'Question Text' },
        { key: 'option_a', label: 'Option A' },
        { key: 'option_b', label: 'Option B' },
        { key: 'option_c', label: 'Option C' },
        { key: 'option_d', label: 'Option D' },
        { key: 'option_e', label: 'Option E' },
        { key: 'correct_answer', label: 'Correct Answer' },
        { key: 'explanation', label: 'Explanation' },
        { key: 'validationScore', label: 'Quality Score' },
        { key: 'generated_by', label: 'Generated By' },
        { key: 'created_at', label: 'Created At' },
        { key: 'updated_at', label: 'Updated At' },
      ]
    );

    // Show success notification
    Swal.fire({
      title: 'Export Successful!',
      html: `<p class="text-gray-600">Successfully exported <strong>${filteredQuestions.length} questions</strong> to CSV.</p>`,
      icon: 'success',
      timer: 2500,
      showConfirmButton: false,
      customClass: {
        popup: 'rounded-2xl',
        title: 'text-2xl font-bold text-gray-900',
      },
    });
  }; */

  const handleExportExcel = async () => {
    if (filteredQuestions.length === 0) {
      Swal.fire({
        title: 'No Questions',
        text: 'There are no questions to export.',
        icon: 'warning',
        confirmButtonColor: '#1e73be',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
          confirmButton: 'rounded-lg px-6 py-3 font-semibold',
        },
      });
      return;
    }

    // Validate and calculate range
    const from = exportFromQuestion || 1;
    const to = exportToQuestion || filteredQuestions.length;

    if (from < 1 || from > filteredQuestions.length) {
      Swal.fire({
        title: 'Invalid Range',
        text: `"From" value must be between 1 and ${filteredQuestions.length}`,
        icon: 'error',
        confirmButtonColor: '#dc2626',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
        },
      });
      return;
    }

    if (to < from || to > filteredQuestions.length) {
      Swal.fire({
        title: 'Invalid Range',
        text: `"To" value must be between ${from} and ${filteredQuestions.length}`,
        icon: 'error',
        confirmButtonColor: '#dc2626',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
        },
      });
      return;
    }

    const exportCount = to - from + 1;

    // Validate date range if provided
    if (exportFromDate && exportToDate) {
      const fromDate = new Date(exportFromDate);
      const toDate = new Date(exportToDate);

      if (fromDate > toDate) {
        Swal.fire({
          title: 'Invalid Date Range',
          text: '"From Date" must be before or equal to "To Date"',
          icon: 'error',
          confirmButtonColor: '#dc2626',
          customClass: {
            popup: 'rounded-2xl',
            title: 'text-2xl font-bold text-gray-900',
          },
        });
        return;
      }
    }

    try {
      // Build filters for export
      const filters: any = {};
      if (examFilter !== 'all') filters.exam = examFilter;
      if (courseFilter !== 'all') filters.course = courseFilter;
      if (typeFilter !== 'all') filters.type = typeFilter;
      if (difficultyFilter !== 'all') filters.difficulty = difficultyFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (sourceFilter !== 'all') filters.sourceType = sourceFilter; // NQG-RQ-26 v2
      if (searchTerm) filters.search = searchTerm;

      // Add range parameters
      filters.fromQuestion = from;
      filters.toQuestion = to;

      // Add date range parameters
      if (exportFromDate) filters.fromDate = exportFromDate;
      if (exportToDate) filters.toDate = exportToDate;

      // Call the export API
      const blob = await questionsService.exportToExcel(filters);

      // Generate timestamp for filename (format: 2024-01-15_14-30-25 - date with time)
      const now = new Date();
      const date = now.toISOString().split('T')[0]; // 2024-01-15
      const time = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // 14-30-25
      const timestamp = `${date}_${time}`;

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `question-bank-${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Build success message
      let successMessage = `Successfully exported <strong>${exportCount} questions</strong> (${from} to ${to})`;
      if (exportFromDate || exportToDate) {
        const dateInfo = [];
        if (exportFromDate) dateInfo.push(`from ${exportFromDate}`);
        if (exportToDate) dateInfo.push(`to ${exportToDate}`);
        successMessage += ` <br/><span class="text-sm">Date range: ${dateInfo.join(' ')}</span>`;
      }
      successMessage += ' to Excel.';

      // Show success notification
      Swal.fire({
        title: 'Export Successful!',
        html: `<p class="text-gray-600">${successMessage}</p>`,
        icon: 'success',
        timer: 3000,
        showConfirmButton: false,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
        },
      });
    } catch (error: any) {
      console.error('Excel export failed:', error);

      // Get the error message from backend
      const errorMessage = error.response?.data?.error || 'Failed to export questions to Excel.';

      Swal.fire({
        title: error.response?.status === 404 ? 'No Questions Found' : 'Export Failed',
        html: `<p class="text-gray-600">${errorMessage}</p>`,
        icon: error.response?.status === 404 ? 'warning' : 'error',
        confirmButtonColor: error.response?.status === 404 ? '#f59e0b' : '#dc2626',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
        },
      });
    }
  };

  // Export ONLY Interactive Questions to Excel
  const handleExportInteractiveQuestions = async () => {
    // Filter for interactive questions only
    const interactiveQuestions = allQuestions.filter(q => q.type === 'interactive');

    if (interactiveQuestions.length === 0) {
      Swal.fire({
        title: 'No Interactive Questions',
        text: 'There are no interactive questions to export.',
        icon: 'warning',
        confirmButtonColor: '#1e73be',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
          confirmButton: 'rounded-lg px-6 py-3 font-semibold',
        },
      });
      return;
    }

    try {
      // Build filters for interactive questions only
      const filters: any = {
        type: 'interactive', // Force interactive type
      };

      // Add other filters if set
      if (examFilter !== 'all') filters.exam = examFilter;
      if (courseFilter !== 'all') filters.course = courseFilter;
      if (difficultyFilter !== 'all') filters.difficulty = difficultyFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (sourceFilter !== 'all') filters.sourceType = sourceFilter; // NQG-RQ-26 v2
      if (oeCodeFilter) filters.oeCode = oeCodeFilter;

      // Call the export API
      const blob = await questionsService.exportToExcel(filters);

      // Generate timestamp for filename
      const now = new Date();
      const date = now.toISOString().split('T')[0];
      const time = now.toTimeString().split(' ')[0].replace(/:/g, '-');
      const timestamp = `${date}_${time}`;

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `interactive-questions-${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success notification
      Swal.fire({
        title: 'Export Successful!',
        html: `<p class="text-gray-600">Successfully exported <strong>${interactiveQuestions.length} interactive questions</strong> to Excel.</p>`,
        icon: 'success',
        timer: 3000,
        showConfirmButton: false,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
        },
      });
    } catch (error: any) {
      console.error('Interactive questions Excel export failed:', error);

      const errorMessage = error.response?.data?.error || 'Failed to export interactive questions to Excel.';

      Swal.fire({
        title: error.response?.status === 404 ? 'No Interactive Questions Found' : 'Export Failed',
        html: `<p class="text-gray-600">${errorMessage}</p>`,
        icon: error.response?.status === 404 ? 'warning' : 'error',
        confirmButtonColor: error.response?.status === 404 ? '#f59e0b' : '#dc2626',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
        },
      });
    }
  };

  // Export ONLY Case Study Questions to Excel
  const handleExportCaseStudyQuestions = async () => {
    // Filter for case study questions only
    const caseStudyQuestions = allQuestions.filter(q => q.type === 'case_study' || q.type === 'CASE_STUDY' || q.type === 'case-study');

    if (caseStudyQuestions.length === 0) {
      Swal.fire({
        title: 'No Case Study Questions',
        text: 'There are no case study questions to export.',
        icon: 'warning',
        confirmButtonColor: '#1e73be',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
          confirmButton: 'rounded-lg px-6 py-3 font-semibold',
        },
      });
      return;
    }

    try {
      // Build filters for case study questions only
      const filters: any = {
        type: 'case_study', // Force case_study type
      };

      // Add other filters if set
      if (examFilter !== 'all') filters.exam = examFilter;
      if (courseFilter !== 'all') filters.course = courseFilter;
      if (difficultyFilter !== 'all') filters.difficulty = difficultyFilter;
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (sourceFilter !== 'all') filters.sourceType = sourceFilter; // NQG-RQ-26 v2
      if (oeCodeFilter) filters.oeCode = oeCodeFilter;

      // Call the export API
      const blob = await questionsService.exportToExcel(filters);

      // Generate timestamp for filename
      const now = new Date();
      const date = now.toISOString().split('T')[0];
      const time = now.toTimeString().split(' ')[0].replace(/:/g, '-');
      const timestamp = `${date}_${time}`;

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `case-study-questions-${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success notification
      Swal.fire({
        title: 'Export Successful!',
        html: `<p class="text-gray-600">Successfully exported <strong>${caseStudyQuestions.length} case study questions</strong> to Excel.</p>`,
        icon: 'success',
        timer: 3000,
        showConfirmButton: false,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
        },
      });
    } catch (error: any) {
      console.error('Case study questions Excel export failed:', error);

      const errorMessage = error.response?.data?.error || 'Failed to export case study questions to Excel.';

      Swal.fire({
        title: error.response?.status === 404 ? 'No Case Study Questions Found' : 'Export Failed',
        html: `<p class="text-gray-600">${errorMessage}</p>`,
        icon: error.response?.status === 404 ? 'warning' : 'error',
        confirmButtonColor: error.response?.status === 404 ? '#f59e0b' : '#dc2626',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
        },
      });
    }
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'MCQ':
      case 'multiple_choice':
        return <Target className="w-4 h-4" />;
      case 'case_study':
      case 'case_based':
        return <BookOpen className="w-4 h-4" />;
      case 'interactive':
        return <Lightbulb className="w-4 h-4" />;
      case 'SHORT':
      case 'short_answer':
      case 'short_question':
        return <FileText className="w-4 h-4" />;
      case 'true_false':
        return <CheckCircle className="w-4 h-4" />;
      case 'decision_tree':
        return <TrendingUp className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'multiple_choice':
        return 'Multiple Choice';
      case 'MCQ':
        return 'MCQ';
      case 'case_study':
        return 'Case Study';
      case 'case_based':
        return 'Case Based';
      case 'short_answer':
        return 'Short Answer';
      case 'SHORT':
        return 'Short Question';
      case 'interactive':
        return 'Interactive';
      case 'true_false':
        return 'True/False';
      case 'decision_tree':
        return 'Decision Tree';
      default:
        return type;
    }
  };


  // Define columns for SortableTable
  const columns: Column<Question>[] = [
    {
      key: 'question_id',
      label: 'Question ID',
      sortable: true,
      render: (val) => (
        <div className="text-sm font-mono font-medium text-gray-900">
          {String(val).substring(0, 8)}
        </div>
      ),
    },
    {
      key: 'question_text',
      label: 'Question Preview',
      sortable: true,
      render: (val) => (
        <div className="text-sm text-gray-900 line-clamp-2 max-w-md">{String(val)}</div>
      ),
    },
    {
      key: 'examName',
      label: 'Exam',
      sortable: true,
      render: (_val, row) => (
        <div className="text-sm text-gray-700">
          {row.examName || <span className="text-gray-400">-</span>}
        </div>
      ),
    },
    {
      key: 'courseName',
      label: 'Course',
      sortable: true,
      render: (_val, row) => (
        <div className="text-sm text-gray-700">
          {row.courseName || <span className="text-gray-400">-</span>}
        </div>
      ),
    },
    {
      key: 'oeCode',
      label: 'OE Codes',
      sortable: true,
      render: (_val, row) => {
        // Combine and deduplicate OE codes
        const allOECodes = Array.from(new Set([
          ...(row.oeCode ? [row.oeCode] : []),
          ...(row.oeCodes || [])
        ]));
        const isExpanded = expandedOECodes.has(row.question_id);
        const displayLimit = 2;
        const codesToDisplay = isExpanded ? allOECodes : allOECodes.slice(0, displayLimit);

        const toggleExpanded = () => {
          setExpandedOECodes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(row.question_id)) {
              newSet.delete(row.question_id);
            } else {
              newSet.add(row.question_id);
            }
            return newSet;
          });
        };

        return (
          <div className="text-sm">
            {allOECodes.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {codesToDisplay.map((code, idx) => (
                  <span
                    key={`${row.question_id}-${code}-${idx}`}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700 border border-blue-300"
                    title={`OE Code: ${code}`}
                  >
                    {code}
                  </span>
                ))}
                {allOECodes.length > displayLimit && (
                  <button
                    onClick={toggleExpanded}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200 hover:text-gray-800 transition-colors cursor-pointer"
                    title={isExpanded ? 'Show less' : `Show ${allOECodes.length - displayLimit} more`}
                  >
                    {isExpanded ? '−' : `+${allOECodes.length - displayLimit}`}
                  </button>
                )}
              </div>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'question_type',
      label: 'Type',
      sortable: true,
      render: (val, row) => (
        <div className="flex items-center space-x-2">
          {getTypeIcon(row.question_type)}
          <span className="text-sm text-gray-900">{getTypeLabel(String(val))}</span>
        </div>
      ),
    },
    {
      key: 'difficulty',
      label: 'Difficulty',
      sortable: true,
      render: (val) => <StatusBadge status={String(val)} />,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (val, row) => (
        <div>
          <StatusBadge status={String(val)} />
          {val === 'approved' && row.reviewed_by && (
            <div className="text-xs text-gray-500 mt-1">
              ✓ By {row.reviewed_by.split('@')[0]}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'topics',
      label: 'Topics',
      sortable: false,
      render: (_val, row) => (
        <div className="text-center">
          {row.topics && row.topics.length > 0 ? (
            <Tooltip content={row.topics.map((t: any) => {
              if (typeof t === 'string') return t;
              const subCode = t.subOECode || '';
              const title = t.title || '';
              if (!subCode && !title) return null;
              if (subCode && title) return `${subCode} - ${title}`;
              return subCode || title;
            }).filter(Boolean).join(', ')}>
              <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full border border-green-200">
                📚 {row.topics.length}
              </span>
            </Tooltip>
          ) : (
            <span className="text-gray-400 text-xs">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'sourceDocuments',
      label: 'Sources',
      sortable: false,
      render: (_val, row) => (
        <div className="text-center">
          {row.sourceDocuments && row.sourceDocuments.length > 0 ? (
            <Tooltip content={row.sourceDocuments.map((d: any) => d.filename).join(', ')}>
              <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full border border-indigo-200">
                📄 {row.sourceDocuments.length}
              </span>
            </Tooltip>
          ) : (
            <span className="text-gray-400 text-xs">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'validationScore',
      label: 'Quality',
      sortable: true,
      render: (val) => val !== undefined && val !== null ? <span className="text-gray-400">-</span> : <span className="text-gray-400">N/A</span>,
    },
    {
      key: 'created_at',
      label: 'Generated',
      sortable: true,
      render: (_val, row) => (
        <div className="text-xs text-gray-500">
          <div className="flex items-center space-x-1 mb-1">
            <Calendar className="w-3 h-3" />
            <span>{formatDate(row.generation_date || row.created_at)}</span>
          </div>
          <div className="text-xs text-gray-400">
            {row.generated_by === 'llm'
              ? '🤖 AI'
              : row.generated_by === 'human'
              ? '👤 Human'
              : '🔄 Hybrid'}
          </div>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_val, row) => (
        <div className="flex items-center space-x-2">
          <Tooltip content="View details">
            <button
              onClick={() => handleViewDetails(row)}
              className="text-blue-600 hover:text-blue-900"
            >
              <Eye className="w-4 h-4" />
            </button>
          </Tooltip>
          <Tooltip content="Edit question">
            <button
              onClick={() => navigate(`/questions/${row.question_id}/edit`)}
              className="text-green-600 hover:text-green-900"
            >
              <Edit3 className="w-4 h-4" />
            </button>
          </Tooltip>
          <Tooltip content="Delete question">
            <button
              onClick={() => handleDelete(row.question_id)}
              className="text-red-600 hover:text-red-900"
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e73be] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading question bank...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header with Breadcrumbs */}
      <div className="flex items-start justify-between">
        <PageHeader
          title="Question Bank"
          description="Browse, filter, and manage all generated questions"
          breadcrumbs={[{ label: 'Question Bank' }]}
        />
        <div className="flex items-center space-x-3 mt-10">
          {/* Export CSV button - hidden for now */}
          {/* <Tooltip content="Export filtered questions to CSV">
            <button onClick={handleExportCSV} className="btn-secondary flex items-center">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>
          </Tooltip> */}

          {/* Export Range & Date Filters */}
          <div className="flex flex-col gap-2">
            {/* Question Range */}
            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              <span className="text-sm font-medium text-gray-700">Range:</span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  max={filteredQuestions.length}
                  value={exportFromQuestion || ''}
                  onChange={(e) => setExportFromQuestion(parseInt(e.target.value) || 1)}
                  placeholder="From"
                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#1e73be] focus:border-transparent"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="number"
                  min={exportFromQuestion || 1}
                  max={filteredQuestions.length}
                  value={exportToQuestion || ''}
                  onChange={(e) => setExportToQuestion(parseInt(e.target.value) || filteredQuestions.length)}
                  placeholder={`To (${filteredQuestions.length})`}
                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#1e73be] focus:border-transparent"
                />
              </div>
            </div>

            {/* Date Range */}
            <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
              <span className="text-sm font-medium text-gray-700">Date:</span>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={exportFromDate}
                  onChange={(e) => setExportFromDate(e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#1e73be] focus:border-transparent"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="date"
                  value={exportToDate}
                  onChange={(e) => setExportToDate(e.target.value)}
                  min={exportFromDate}
                  className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-[#1e73be] focus:border-transparent"
                />
              </div>
              <Tooltip content={`Export questions${exportFromDate ? ` from ${exportFromDate}` : ''}${exportToDate ? ` to ${exportToDate}` : ''}`}>
                <button onClick={handleExportExcel} className="btn-secondary flex items-center ml-2">
                  <Download className="w-4 h-4 mr-2" />
                  Export Excel
                </button>
              </Tooltip>
            </div>

            {/* Interactive Questions Export Button */}
            <Tooltip content="Export ONLY interactive questions to Excel">
              <button
                onClick={handleExportInteractiveQuestions}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center font-semibold transition-colors"
              >
                <Target className="w-4 h-4 mr-2" />
                Export Interactive
              </button>
            </Tooltip>

            {/* Case Study Questions Export Button */}
            <Tooltip content="Export ONLY case study questions to Excel">
              <button
                onClick={handleExportCaseStudyQuestions}
                className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg flex items-center font-semibold transition-colors"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Export Case Study
              </button>
            </Tooltip>
          </div>
          {allQuestions.length > 0 && (
            <Tooltip content="Delete all questions from database">
              <button
                onClick={handleDeleteAll}
                disabled={deleteAllMutation.isPending}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {deleteAllMutation.isPending ? 'Deleting...' : 'Delete All'}
              </button>
            </Tooltip>
          )}
          <Tooltip content="Generate a new question">
            <button
              onClick={() => navigate('/generator-center')}
              className="btn-primary flex items-center"
            >
              <Lightbulb className="w-4 h-4 mr-2" />
              Generate New
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Tooltip content="Total questions matching current filters">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Questions</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString()}</p>
              </div>
              <Database className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </Tooltip>

        <Tooltip content="Questions approved for use">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Approved</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.approved.toLocaleString()}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </Tooltip>

        <Tooltip content="Questions pending review">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Under Review</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {stats.underReview.toLocaleString()}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
        </Tooltip>

        <Tooltip content="Draft questions">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Drafts</p>
                <p className="text-2xl font-bold text-gray-600">{stats.draft.toLocaleString()}</p>
              </div>
              <FileText className="w-8 h-8 text-gray-600" />
            </div>
          </div>
        </Tooltip>

        <Tooltip content="Average quality score of all questions">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Avg Quality</p>
                <p className="text-2xl font-bold text-[#ea7500]">
                  {stats.avgQuality.toFixed(1)}/10
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-[#ea7500]" />
            </div>
          </div>
        </Tooltip>
      </div>

      {/* Filters */}
      <div className="card space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          </div>
          <div className="flex items-center space-x-3">
            <Tooltip content="Refresh questions from database">
              <button
                onClick={() => {
                  queryClient.invalidateQueries({ queryKey: ['questions'] });
                  Swal.fire({
                    title: 'Refreshed!',
                    text: 'Questions list has been refreshed.',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false,
                  });
                }}
                className="text-sm text-[#1e73be] hover:text-[#002258] font-medium flex items-center gap-1"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </Tooltip>
            <Tooltip content="Clear all filters">
              <button
                onClick={handleResetFilters}
                className="text-sm text-[#1e73be] hover:text-[#002258] font-medium"
              >
                Reset All
              </button>
            </Tooltip>
            <ViewToggle view={view} onViewChange={setView} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-9 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 input-field"
              />
            </div>
          </div>

          {/* Exam Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Exam</label>
            <select
              value={examFilter}
              onChange={(e) => {
                setExamFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field w-full"
            >
              <option value="all">All Exams</option>
              {EXAMS.map((exam) => (
                <option key={exam} value={exam}>
                  {exam}
                </option>
              ))}
            </select>
          </div>

          {/* Course Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Course</label>
            <select
              value={courseFilter}
              onChange={(e) => {
                setCourseFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field w-full"
            >
              <option value="all">All Courses</option>
              {(coursesData || []).map((course: any) => (
                <option key={course.course_id} value={course.course_id}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>

          {/* OE Code Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">OE Code</label>
            <input
              type="text"
              placeholder="e.g., OE.01"
              value={oeCodeFilter}
              onChange={(e) => {
                setOeCodeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field w-full"
            />
          </div>

          {/* Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field w-full"
            >
              <option value="all">All Types</option>
              <option value="multiple_choice">Multiple Choice (MCQ)</option>
              <option value="MCQ">Multiple Choice (Legacy)</option>
              <option value="case_study">Case Study</option>
              <option value="case_based">Case Based</option>
              <option value="short_answer">Short Answer</option>
              <option value="SHORT">Short (Legacy)</option>
              <option value="interactive">Interactive</option>
            </select>
          </div>

          {/* Difficulty Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
            <select
              value={difficultyFilter}
              onChange={(e) => {
                setDifficultyFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field w-full"
            >
              <option value="all">All Levels</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field w-full"
            >
              <option value="all">All Status</option>
              <option value="generated">Generated</option>
              <option value="approved">Approved</option>
              <option value="under_review">Under Review</option>
              <option value="draft">Draft</option>
              <option value="rejected">Rejected</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Generated By Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Generated By</label>
            <select
              value={generatedByFilter}
              onChange={(e) => {
                setGeneratedByFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field w-full"
            >
              <option value="all">All Sources</option>
              <option value="llm">AI Generated</option>
              <option value="human">Human Created</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>

          {/* Source Filter (NQG-RQ-26 v2: standard vs free PDF source) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <select
              value={sourceFilter}
              onChange={(e) => {
                setSourceFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field w-full"
            >
              <option value="all">All sources</option>
              <option value="standard">Standard</option>
              <option value="free_pdf">Free PDF source</option>
            </select>
          </div>

        </div>

        <div className="flex items-center justify-between text-sm text-gray-600 pt-2 border-t border-gray-200">
          <p>
            Showing <span className="font-semibold text-gray-900">{filteredQuestions.length}</span>{' '}
            questions
            {searchTerm && (
              <span>
                {' '}
                matching <span className="font-semibold">"{searchTerm}"</span>
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Questions Content */}
      {filteredQuestions.length === 0 ? (
        <EmptyState
          icon={Database}
          title="No questions found"
          description="There are no questions matching your current filters. Try adjusting your filters or generate a new question to get started."
          action={{
            label: 'Generate New Question',
            to: '/generator-center',
          }}
          secondaryAction={{
            label: 'Reset Filters',
            onClick: handleResetFilters,
          }}
        />
      ) : view === 'list' ? (
        <div className="card overflow-hidden p-0">
          <SortableTable
            columns={columns}
            data={paginatedQuestions}
            keyExtractor={(q) => q.question_id}
            emptyMessage="No questions to display"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginatedQuestions.map((question) => (
            <div
              key={question.question_id}
              className="card hover:shadow-lg transition-shadow border-l-4 border-[#1e73be]"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3 flex-1">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    {getTypeIcon(question.question_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs font-mono text-gray-500 break-all">
                      {question.question_id}
                    </h3>
                    {question.examName && (
                      <p className="text-xs text-gray-500 mt-1">{question.examName}</p>
                    )}
                    {question.courseName && (
                      <p className="text-xs text-blue-600 font-medium mt-0.5">{question.courseName}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Question Text */}
              <p className="text-sm text-gray-900 mb-3 line-clamp-3">{question.question_text}</p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-3">
                <StatusBadge status={question.difficulty} />
                <StatusBadge status={question.status} />
                <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full border border-gray-200">
                  {getTypeLabel(question.question_type)}
                </span>
                {question.topics && question.topics.length > 0 && (
                  <Tooltip content={question.topics.map((t: any) => {
                    if (typeof t === 'string') return t;
                    const subCode = t.subOECode || '';
                    const title = t.title || '';
                    if (!subCode && !title) return null;
                    if (subCode && title) return `${subCode} - ${title}`;
                    return subCode || title;
                  }).filter(Boolean).join(', ')}>
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full border border-green-200">
                      📚 {question.topics.length} {question.topics.length === 1 ? 'topic' : 'topics'}
                    </span>
                  </Tooltip>
                )}
                {question.sourceDocuments && question.sourceDocuments.length > 0 && (
                  <Tooltip content={question.sourceDocuments.map((d: any) => d.filename).join(', ')}>
                    <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full border border-indigo-200">
                      📄 {question.sourceDocuments.length} {question.sourceDocuments.length === 1 ? 'source' : 'sources'}
                    </span>
                  </Tooltip>
                )}
                {/* NQG-RQ-26 v2: free PDF source flag */}
                {question.sourceType === 'free_pdf' && (
                  <Tooltip content="Synced from the PDF question bank">
                    <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full border border-indigo-200">
                      📑 PDF source
                    </span>
                  </Tooltip>
                )}
              </div>

              {/* Source Files - Visible List - Commented out, only show in detail modal */}
              {/* {question.sourceDocuments && question.sourceDocuments.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-600 mb-1">Source Files:</p>
                  <div className="flex flex-wrap gap-1">
                    {question.sourceDocuments.slice(0, 2).map((doc: any, idx: number) => (
                      <span
                        key={doc.documentId || idx}
                        className="inline-flex items-center px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded text-xs"
                        title={doc.title}
                      >
                        📄 {doc.filename}
                      </span>
                    ))}
                    {question.sourceDocuments.length > 2 && (
                      <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                        +{question.sourceDocuments.length - 2} more
                      </span>
                    )}
                  </div>
                </div>
              )} */}

              {/* OE Codes */}
              {(() => {
                // Combine and deduplicate OE codes
                const allOECodes = Array.from(new Set([
                  ...(question.oeCode ? [question.oeCode] : []),
                  ...(question.oeCodes || [])
                ]));
                const isExpanded = expandedOECodes.has(question.question_id);
                const displayLimit = 2;
                const codesToDisplay = isExpanded ? allOECodes : allOECodes.slice(0, displayLimit);

                const toggleExpanded = () => {
                  setExpandedOECodes(prev => {
                    const newSet = new Set(prev);
                    if (newSet.has(question.question_id)) {
                      newSet.delete(question.question_id);
                    } else {
                      newSet.add(question.question_id);
                    }
                    return newSet;
                  });
                };

                return allOECodes.length > 0 ? (
                  <div className="mb-3 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Code2 className="w-3.5 h-3.5 text-blue-600" />
                      <span className="text-xs font-semibold text-blue-700">OE Codes:</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {codesToDisplay.map((code, idx) => (
                        <span
                          key={`${question.question_id}-oe-${code}-${idx}`}
                          className="inline-flex items-center px-2.5 py-1 rounded text-xs font-bold bg-blue-100 text-blue-700 border border-blue-300 hover:bg-blue-200 transition-colors"
                          title={`OE Code: ${code}`}
                        >
                          {code}
                        </span>
                      ))}
                      {allOECodes.length > displayLimit && (
                        <button
                          onClick={toggleExpanded}
                          className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200 hover:text-gray-800 transition-colors cursor-pointer"
                          title={isExpanded ? 'Show less' : `Show ${allOECodes.length - displayLimit} more`}
                        >
                          {isExpanded ? '−' : `+${allOECodes.length - displayLimit}`}
                        </button>
                      )}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Details */}
              <div className="space-y-2 mb-4 text-xs">
                {question.correct_answer && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Correct Answer:</span>
                    <span className="font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded">
                      {question.correct_answer}
                    </span>
                  </div>
                )}
                {question.created_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="font-medium text-gray-900">
                      {formatDate(question.created_at)}
                    </span>
                  </div>
                )}
                {question.reviewed_by && question.status === 'approved' && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Reviewed by:</span>
                    <span className="font-medium text-gray-900">
                      {question.reviewed_by.split('@')[0]}
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center space-x-2">
                  <Tooltip content="View full details">
                    <button
                      onClick={() => handleViewDetails(question)}
                      className="flex-1 btn-secondary text-xs py-2 flex items-center justify-center"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View
                    </button>
                  </Tooltip>
                  <Tooltip content="Edit question">
                    <button
                      onClick={() => navigate(`/questions/${question.question_id}/edit`)}
                      className="flex-1 btn-secondary text-xs py-2 flex items-center justify-center"
                    >
                      <Edit3 className="w-3 h-3 mr-1" />
                      Edit
                    </button>
                  </Tooltip>
                  <Tooltip content="Delete question">
                    <button
                      onClick={() => handleDelete(question.question_id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {totalItems > 0 && (
        <PaginationControls
          mode={paginationMode}
          onModeChange={handlePaginationModeChange}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onLoadMore={handleLoadMore}
          hasMore={displayCount < totalItems}
          isLoading={isLoading}
        />
      )}

      {/* Details Modal - keeping existing modal code */}
      {showDetailsModal && selectedQuestion && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
             onClick={() => setShowDetailsModal(false)}
            />

            <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Question Details</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Question Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Question ID</p>
                    <p className="text-sm font-mono font-medium">{selectedQuestion.question_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Exam</p>
                    <p className="text-sm font-medium">{selectedQuestion.exam_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Type</p>
                    <div className="flex items-center space-x-2">
                      {getTypeIcon(selectedQuestion.question_type)}
                      <p className="text-sm font-medium">{getTypeLabel(selectedQuestion.question_type)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Difficulty</p>
                    <StatusBadge status={selectedQuestion.difficulty} />
                  </div>
                  {selectedQuestion.topics && selectedQuestion.topics.length > 0 && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 mb-1">Topics</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedQuestion.topics.map((topic, idx) => {
                          // Handle both string (old format) and object (new format)
                          const topicTitle = typeof topic === 'string' ? topic : (topic.title || '');
                          const subOE = typeof topic === 'object' && topic.subOECode ? topic.subOECode : null;

                          // Skip if both title and subOE are empty
                          if (!topicTitle && !subOE) return null;

                          return (
                            <span
                              key={idx}
                              className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full border border-green-200 flex items-center gap-1"
                            >
                              {subOE && (
                                <span className="text-[10px] font-semibold text-green-600 bg-green-200 px-1.5 py-0.5 rounded">
                                  {subOE.replace('OE.', '')}
                                </span>
                              )}
                              {topicTitle && <span>{topicTitle}</span>}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* NQG-RQ-26 v2: free PDF source flag + traceability */}
                {selectedQuestion.sourceType === 'free_pdf' && (
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold bg-indigo-100 text-indigo-700 rounded-lg border border-indigo-200"
                      title="Synced from the PDF question bank"
                    >
                      📑 PDF source
                    </span>
                  </div>
                )}
                {hasQuestionSourceInfo(selectedQuestion) && (
                  <QuestionSourceInfo
                    sourceDocuments={selectedQuestion.sourceDocuments}
                    sourceRef={selectedQuestion.sourceRef}
                    cognitiveTaxonomy={selectedQuestion.cognitiveTaxonomy}
                    topicWeighting={selectedQuestion.topicWeighting}
                  />
                )}

                {/* Case Study Full Display (ANBIMA Format) — Context + All Sub-Questions */}
                {(selectedQuestion.question_type === 'case_study' || selectedQuestion.question_type === 'case_based') && selectedQuestion.caseContext && (
                  <div className="space-y-4">
                    {/* Case Study Context Header */}
                    <div className="p-5 bg-blue-50 border-2 border-blue-300 rounded-lg">
                      {/* Metadados (ANBIMA Metadata Header) */}
                      {(selectedQuestion as any).metadados && (
                        <div className="mb-4 p-3 bg-white rounded border border-blue-200">
                          <p className="text-xs font-semibold text-blue-800 mb-2">📋 Metadados (ANBIMA):</p>
                          <div className="grid grid-cols-2 gap-2 text-xs text-blue-900">
                            {Object.entries((selectedQuestion as any).metadados).map(([key, value]) => (
                              <div key={key}>
                                <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                                <span className="font-semibold text-blue-700">{String(value)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedQuestion.caseTitle && (
                        <h4 className="text-lg font-bold text-blue-900 mb-3 flex items-center">
                          <FileText className="w-5 h-5 mr-2" />
                          {selectedQuestion.caseTitle}
                        </h4>
                      )}

                      {/* Render context with markdown table support */}
                      <div className="text-sm text-blue-900 leading-relaxed mb-3">
                        {(() => {
                          const context = selectedQuestion.caseContext!;
                          const lines = context.split('\n');
                          let inTable = false;
                          const elements: JSX.Element[] = [];
                          let tableRows: string[] = [];
                          let currentParagraph: string[] = [];
                          let key = 0;

                          const flushParagraph = () => {
                            if (currentParagraph.length > 0) {
                              elements.push(
                                <div key={`p-${key++}`} className="whitespace-pre-wrap mb-2">
                                  {currentParagraph.join('\n')}
                                </div>
                              );
                              currentParagraph = [];
                            }
                          };

                          const flushTable = () => {
                            if (tableRows.length > 0) {
                              const headerRow = tableRows[0].split('|').filter(c => c.trim()).map(c => c.trim());
                              const dataRows = tableRows.slice(2).map(row =>
                                row.split('|').filter(c => c.trim()).map(c => c.trim())
                              );

                              elements.push(
                                <table key={`table-${key++}`} className="w-full my-3 text-xs border border-blue-300">
                                  <thead className="bg-blue-200">
                                    <tr>
                                      {headerRow.map((header, idx) => (
                                        <th key={idx} className="border border-blue-300 px-2 py-1 font-semibold text-blue-900">
                                          {header}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {dataRows.map((row, rowIdx) => (
                                      <tr key={rowIdx} className="even:bg-blue-50">
                                        {row.map((cell, cellIdx) => (
                                          <td key={cellIdx} className="border border-blue-300 px-2 py-1">
                                            {cell}
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              );
                              tableRows = [];
                            }
                          };

                          lines.forEach(line => {
                            if (line.includes('|')) {
                              flushParagraph();
                              inTable = true;
                              tableRows.push(line);
                            } else if (inTable && line.trim() === '') {
                              flushTable();
                              inTable = false;
                            } else if (inTable) {
                              flushTable();
                              inTable = false;
                              currentParagraph.push(line);
                            } else {
                              currentParagraph.push(line);
                            }
                          });

                          flushParagraph();
                          flushTable();

                          return elements.length > 0 ? elements : context;
                        })()}
                      </div>

                      {selectedQuestion.caseData && Object.keys(selectedQuestion.caseData).length > 0 && (
                        <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                          <p className="text-xs font-semibold text-blue-800 mb-2">Dados do Caso:</p>
                          <div className="grid grid-cols-2 gap-2 text-xs text-blue-900">
                            {Object.entries(selectedQuestion.caseData).map(([key, value]) => (
                              <div key={key}>
                                <span className="font-medium">{key}:</span>{' '}
                                <span>{String(value)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Sub-Questions */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <Target className="w-4 h-4 mr-2" />
                        Sub-Questions ({caseStudySiblings.length > 0 ? caseStudySiblings.length : (selectedQuestion.totalSubQuestions || 1)})
                      </h4>
                      <div className="space-y-4">
                        {(caseStudySiblings.length > 0 ? caseStudySiblings : [selectedQuestion]).map((subQ, subIdx) => (
                          <div
                            key={subQ.question_id}
                            className="p-4 border-2 border-gray-200 rounded-lg bg-white"
                          >
                            {/* Sub-question header */}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold">
                                  {subQ.subQuestionIndex || subQ.caseQuestionNumber || subIdx + 1}
                                </span>
                                <span className="text-sm font-semibold text-gray-900">
                                  Questão {subQ.subQuestionIndex || subQ.caseQuestionNumber || subIdx + 1}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                {subQ.oeCode && subQ.oeCode !== 'N/A' && (
                                  <span className="px-2 py-0.5 text-[10px] font-semibold bg-green-100 text-green-700 rounded border border-green-200">
                                    {subQ.oeCode}
                                  </span>
                                )}
                                <StatusBadge status={subQ.difficulty} />
                              </div>
                            </div>

                            {/* Question text */}
                            <p className="text-gray-900 mb-4 text-sm leading-relaxed">{subQ.question_text}</p>

                            {/* Alternatives */}
                            <div className="space-y-2 mb-4">
                              {subQ.alternatives && subQ.alternatives.length > 0 ? (
                                subQ.alternatives.map((alt: any) => {
                                  const isCorrect = alt.isCorrect || alt.correct;
                                  return (
                                    <div
                                      key={alt.letter}
                                      className={`p-3 rounded-lg border-2 transition-all ${
                                        isCorrect
                                          ? 'bg-green-50 border-green-300'
                                          : 'bg-gray-50 border-gray-200'
                                      }`}
                                    >
                                      <div className="flex items-start">
                                        {isCorrect ? (
                                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mr-2" />
                                        ) : (
                                          <XCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mr-2" />
                                        )}
                                        <span className="font-bold mr-2">{alt.letter})</span>
                                        <span className="flex-1">{formatAlternativeText(alt.text)}</span>
                                        {isCorrect && (
                                          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-600 text-white ml-2">
                                            Correct
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                Object.entries(subQ.options || {}).map(([key, value]) => {
                                  const isCorrect = key === subQ.correct_option;
                                  return (
                                    <div
                                      key={key}
                                      className={`p-3 rounded-lg border-2 transition-all cursor-default ${
                                        isCorrect
                                          ? 'bg-green-50 border-green-300'
                                          : 'bg-gray-50 border-gray-200'
                                      }`}
                                    >
                                      <div className="flex items-start">
                                        {isCorrect ? (
                                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mr-2" />
                                        ) : (
                                          <XCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mr-2" />
                                        )}
                                        <span className="font-bold mr-2">{key})</span>
                                        <span className="flex-1">{formatAlternativeText(value as string)}</span>
                                        {isCorrect && (
                                          <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-600 text-white ml-2">
                                            Correct
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>

                            {/* Explanation */}
                            {subQ.explanation && (
                              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <h5 className="text-xs font-semibold text-blue-900 mb-1 flex items-center">
                                  <Lightbulb className="w-3.5 h-3.5 mr-1.5" />
                                  Explanation
                                </h5>
                                <p className="text-xs text-blue-900 leading-relaxed whitespace-pre-wrap">
                                  {subQ.explanation}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                               {/* Interactive Question Display (ANBIMA Type 4) */}
                {selectedQuestion.question_type === 'interactive' && (selectedQuestion as any).prompts && (
                  <div className="p-4 border-2 border-gray-200 rounded-lg">
                    <InteractiveQuestionPlayer
                      question={selectedQuestion as any}
                      onComplete={(score, maxScore, _history) => {
                        console.log('[InteractivePlayer] Completed:', score, '/', maxScore);
                        Swal.fire({
                          title: 'Interação Completa!',
                          html: `<p>Sua Pontuação: <strong>${score}/${maxScore}</strong></p>`,
                          icon: 'success',
                          confirmButtonText: 'OK'
                        });
                      }}
                      onReset={() => {
                        console.log('[InteractivePlayer] Reset');
                      }}
                    />
                  </div>
                )}

                {/* Question Content (MCQ/Short Answer — excludes case_study and interactive, handled above) */}
                {selectedQuestion.question_type !== 'interactive' && selectedQuestion.question_type !== 'case_study' && selectedQuestion.question_type !== 'case_based' && (
                  <div className="p-4 border-2 border-gray-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Question</h4>
                    <p className="text-gray-900 mb-4">{selectedQuestion.question_text}</p>

                  <div className="space-y-2 mb-4">
                    {/* Display alternatives - check for both new (alternatives array) and old (options object) format */}
                    {selectedQuestion.alternatives && selectedQuestion.alternatives.length > 0 ? (
                      selectedQuestion.alternatives.map((alt: any) => {
                        const isCorrect = alt.isCorrect || alt.correct;

                        return (
                          <div
                            key={alt.letter}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              isCorrect
                                ? 'bg-green-50 border-green-300'
                                : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-start">
                              {isCorrect ? (
                                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mr-2" />
                              ) : (
                                <XCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mr-2" />
                              )}
                              <span className="font-bold mr-2">{alt.letter})</span>
                              <span className="flex-1">{formatAlternativeText(alt.text)}</span>
                              {isCorrect && (
                                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-600 text-white ml-2">
                                  Correct
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      /* Old format: options object */
                      Object.entries(selectedQuestion.options || {}).map(([key, value]) => {
                        const isCorrect = key === selectedQuestion.correct_option;

                        return (
                          <div
                            key={key}
                            className={`p-3 rounded-lg border-2 transition-all cursor-default ${
                              isCorrect
                                ? 'bg-green-50 border-green-300'
                                : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-start">
                              {isCorrect ? (
                                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mr-2" />
                              ) : (
                                <XCircle className="w-5 h-5 text-gray-400 flex-shrink-0 mr-2" />
                              )}
                              <span className="font-bold mr-2">{key})</span>
                              <span className="flex-1">{formatAlternativeText(value as string)}</span>
                              {isCorrect && (
                                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-600 text-white ml-2">
                                  Correct
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Unified Explanation - Shows why correct answer is right AND why each incorrect answer is wrong */}
                  {selectedQuestion.explanation && (
                    <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                      <h5 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
                        <Lightbulb className="w-4 h-4 mr-2" />
                        Explanation
                      </h5>
                      <p className="text-sm text-blue-900 leading-relaxed whitespace-pre-wrap">
                        {selectedQuestion.explanation}
                      </p>
                    </div>
                  )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button onClick={() => setShowDetailsModal(false)} className="btn-secondary">
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      navigate(`/questions/${selectedQuestion.question_id}/edit`);
                    }}
                    className="btn-primary flex items-center"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit Question
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
